'use client'

import { useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import type { Industry } from '@/types/exam'
import { IndustrySpot } from '@/components/layout/IndustrySpot'
import { FlashcardDeckCard, type DeckSummary } from './FlashcardDeckCard'

interface FlashcardDirectoryProps {
  decks: DeckSummary[]
}

/** Group decks by exam domain, largest domain first, decks A-Z within. */
function groupByIndustry(decks: DeckSummary[]): [Industry, DeckSummary[]][] {
  const map = new Map<Industry, DeckSummary[]>()
  for (const deck of decks) {
    const list = map.get(deck.industry)
    if (list) list.push(deck)
    else map.set(deck.industry, [deck])
  }
  return [...map.entries()]
    .map(
      ([industry, group]) =>
        [industry, [...group].sort((a, b) => a.name.localeCompare(b.name))] as [
          Industry,
          DeckSummary[],
        ]
    )
    .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
}

function matches(deck: DeckSummary, q: string): boolean {
  const hay = [
    deck.name,
    deck.fullName,
    deck.industry,
    deck.org,
    ...deck.topics,
  ]
    .join(' ')
    .toLowerCase()
  return q
    .toLowerCase()
    .split(/\s+/)
    .every((term) => hay.includes(term))
}

/**
 * Client directory: a search bar over the deck cards (by exam, domain, org, or
 * topic), then the results grouped by domain. Mirrors the exam browser's
 * search affordance. Data is passed as lean summaries to keep the payload
 * small on mobile.
 */
export function FlashcardDirectory({ decks }: FlashcardDirectoryProps) {
  const [query, setQuery] = useState('')
  const trimmed = query.trim()

  const filtered = useMemo(
    () => (trimmed ? decks.filter((d) => matches(d, trimmed)) : decks),
    [decks, trimmed]
  )
  const groups = useMemo(() => groupByIndustry(filtered), [filtered])

  return (
    <div>
      <div className="mx-auto mb-10 max-w-xl">
        <div className="border-border bg-bg shadow-card focus-within:border-primary-border flex h-12 items-center gap-3 rounded-full border px-4 transition-colors">
          <Search size={16} className="text-hint shrink-0" aria-hidden />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by exam, domain, or topic"
            aria-label="Search flashcard decks by exam, domain, or topic"
            className="text-ink placeholder:text-hint h-full w-full min-w-0 flex-1 bg-transparent text-[15px] focus:outline-none"
          />
          {trimmed && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="text-hint hover:bg-surface hover:text-body -mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors"
            >
              <X size={14} aria-hidden />
            </button>
          )}
        </div>
      </div>

      {groups.length === 0 ? (
        <p className="text-body py-10 text-center text-sm">
          No decks match{' '}
          <span className="text-ink font-semibold">
            &ldquo;{trimmed}&rdquo;
          </span>
          . Try an exam name, a domain like Nursing, or a topic.
        </p>
      ) : (
        <div className="space-y-12">
          {groups.map(([industry, group]) => (
            <div key={industry}>
              <div className="mb-5 flex items-center gap-2.5">
                <IndustrySpot industry={industry} />
                <h2 className="font-heading text-ink text-xl font-bold">
                  {industry}
                </h2>
                <span className="text-hint text-sm font-medium tabular-nums">
                  {group.length} {group.length === 1 ? 'deck' : 'decks'}
                </span>
              </div>
              <div className="grid gap-5 lg:grid-cols-2">
                {group.map((deck) => (
                  <FlashcardDeckCard key={deck.slug} deck={deck} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
